import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function PaymentPage() {
  const api = useApi();
  const { currentUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [allBrackets, setAllBrackets] = useState([]);
  const [myBrackets, setMyBrackets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('March Madness 2026');
  const [loading, setLoading] = useState(true);
  const [launched, setLaunched] = useState(null); // 'paypal' | 'venmo'
  const [error, setError] = useState('');
  const [showOthers, setShowOthers] = useState(false);
  const [othersLoading, setOthersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Step 1: payment config (public, no auth needed)
      let cfg = null;
      try {
        const r = await fetch(`${API_BASE}/payment-config`);
        cfg = r.ok ? await r.json() : { enabled: false };
      } catch (_) { cfg = { enabled: false }; }
      if (cancelled) return;
      setConfig(cfg);
      if (!cfg.enabled) { setLoading(false); return; }
      const fee = Number(cfg.entryFee) || 0;

      // Step 2: Firebase token
      let token = null;
      try {
        const { getAuth } = await import('firebase/auth');
        const user = getAuth().currentUser;
        if (user) token = await user.getIdToken();
      } catch (_) {}
      if (cancelled) return;
      if (!token) { setError('Not authenticated.'); setLoading(false); return; }

      // Step 3: brackets
      let myAll = [];
      try {
        const r = await fetch(`${API_BASE}/brackets`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const d = await r.json(); myAll = Array.isArray(d) ? d : []; }
      } catch (e) { setError(e.message); }
      if (cancelled) return;

      const mine = myAll.filter(b => !b.paid);
      setMyBrackets(myAll);
      setSelected(new Set(mine.map(b => b.id)));
      setAmount(String(mine.length * fee));
      setLoading(false);
    }
    load().catch(e => {
      if (!cancelled) { setError(e.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, []);

  // Recalculate amount when selection changes
  useEffect(() => {
    if (!config) return;
    const fee = Number(config.entryFee) || 0;
    setAmount(String(selected.size * fee));
  }, [selected, config]);

  function toggleBracket(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function buildPayPalUrl() {
    const base = config.paypalUrl.replace(/\/$/, '');
    const amt = Number(amount) || 0;
    return amt > 0 ? `${base}/${amt}` : base;
  }

  function buildVenmoUrl() {
    const username = config.venmoUsername;
    const amt = Number(amount) || 0;
    const allKnown = [...myBrackets, ...allBrackets];
    const selectedNames = [...selected]
      .map(id => allKnown.find(b => b.id === id)?.name)
      .filter(Boolean);
    const fullNote = selectedNames.length > 0
      ? `${note || 'March Madness 2026'} - ${selectedNames.join(', ')}`
      : (note || 'March Madness 2026');
    const encodedNote = encodeURIComponent(fullNote);
    return `https://venmo.com/u/${username}?txn=pay&amount=${amt}&note=${encodedNote}`;
  }

  function handlePayPal() {
    window.open(buildPayPalUrl(), '_blank');
    setLaunched('paypal');
  }

  function handleVenmo() {
    window.open(buildVenmoUrl(), '_blank');
    setLaunched('venmo');
  }

  if (loading || config === null) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading payment info...</div>
    </div>
  );

  if (!config.enabled) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">ENTRY <span>PAYMENT</span></h1>
      </div>
      <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--gray)' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>&#x1F512;</div>
        <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>
          Payments are not currently enabled for this pool.
        </p>
      </div>
    </div>
  );

  const fee = Number(config.entryFee) || 0;
  const amountNum = Number(amount) || 0;
  const hasPayPal = !!config.paypalUrl;
  const hasVenmo = !!config.venmoUsername;
  const hasContact = !!config.contactEmail;



  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">ENTRY <span>PAYMENT</span></h1>
        <p className="page-subtitle">
          Entry fee: <strong>${fee} per bracket</strong>
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {launched && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <strong>Payment window opened!</strong> Once your payment is received, the pool admin will mark your bracket(s) as paid. This may take a day or two.
        </div>
      )}

      <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>

        {/* Left: bracket selection */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title" style={{ fontSize: '1.1rem' }}>SELECT BRACKETS TO PAY FOR</h2>
          </div>

          {/* My unpaid brackets */}
          {myBrackets.filter(b => !b.paid).length === 0 && myBrackets.filter(b => b.paid).length === 0 ? (
            <div style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', padding: '12px 0' }}>
              No brackets found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

              {/* Unpaid brackets - selectable */}
              {myBrackets.filter(b => !b.paid).map(b => (
                <label key={b.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '9px 4px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  cursor: 'pointer',
                  background: selected.has(b.id) ? 'rgba(75,139,190,0.07)' : 'transparent',
                }}>
                  <input type="checkbox" checked={selected.has(b.id)}
                    onChange={() => toggleBracket(b.id)} />
                  <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--cream)', flex: 1 }}>
                    {b.name}
                  </span>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--blue)' }}>
                    ${fee}
                  </span>
                </label>
              ))}

              {/* Already paid brackets - greyed out, non-selectable */}
              {myBrackets.filter(b => b.paid).length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>
                    Already Paid
                  </div>
                  {myBrackets.filter(b => b.paid).map(b => (
                    <div key={b.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '7px 4px',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      opacity: 0.45,
                    }}>
                      <span style={{ width: 16, textAlign: 'center', color: '#27ae60', fontSize: '0.85rem' }}>$</span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--gray)', flex: 1, textDecoration: 'line-through' }}>
                        {b.name}
                      </span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--gray)' }}>
                        Paid
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Toggle for other people's brackets */}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <button
                  onClick={async () => {
                    if (!showOthers && allBrackets.length === 0) {
                      setOthersLoading(true);
                      try {
                        const { getAuth } = await import('firebase/auth');
                        const user = getAuth().currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (token) {
                          const r = await fetch(`${API_BASE}/admin/brackets`, { headers: { Authorization: `Bearer ${token}` } });
                          if (r.ok) {
                            const d = await r.json();
                            const all = Array.isArray(d) ? d : (d.brackets || []);
                            setAllBrackets(all.filter(b => !b.paid && b.userId !== currentUser?.uid));
                          }
                        }
                      } catch (_) {}
                      setOthersLoading(false);
                    }
                    setShowOthers(s => !s);
                  }}
                  style={{ background: 'none', border: '1px solid var(--slate)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {showOthers ? '▲' : '▼'} {showOthers ? 'Hide' : 'Show'} other participants' unpaid entries
                  {allBrackets.length > 0 && <span style={{ color: '#e67e22', fontWeight: 700 }}>({allBrackets.length})</span>}
                </button>

                {othersLoading && (
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)', padding: '8px 4px' }}>
                    Loading...
                  </div>
                )}
                {showOthers && (
                  <div style={{ marginTop: 8 }}>
                    {allBrackets.length === 0 ? (
                      <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)', padding: '8px 4px' }}>
                        No other unpaid brackets.
                      </div>
                    ) : (
                      <>
                        {/* Group others by user */}
                        {Object.values(
                          allBrackets.reduce((groups, b) => {
                            const key = b.userName || b.userEmail || b.userId;
                            if (!groups[key]) groups[key] = { label: key, brackets: [] };
                            groups[key].brackets.push(b);
                            return groups;
                          }, {})
                        ).sort((a, b) => a.label.localeCompare(b.label)).map((group, gi) => (
                          <div key={gi} style={{ marginBottom: 4 }}>
                            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '6px 4px 2px' }}>
                              {group.label}
                            </div>
                            {group.brackets.map(b => (
                              <label key={b.id} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '8px 4px',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                cursor: 'pointer',
                                background: selected.has(b.id) ? 'rgba(75,139,190,0.07)' : 'transparent',
                              }}>
                                <input type="checkbox" checked={selected.has(b.id)}
                                  onChange={() => toggleBracket(b.id)} />
                                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--cream)', flex: 1 }}>
                                  {b.name}
                                </span>
                                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--blue)' }}>
                                  ${fee}
                                </span>
                              </label>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}

          {/* Amount + note overrides */}
          <div style={{ marginTop: 20, borderTop: '1px solid var(--slate)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">
                Amount ($)
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)', marginLeft: 8, fontWeight: 400 }}>
                  Editable &mdash; adjust if paying for future entries or any other amount
                </span>
              </label>
              <input className="form-input" type="number" min={0} step={1}
                value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0" style={{ maxWidth: 160 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">
                Payment Note
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.72rem', color: 'var(--gray)', marginLeft: 8, fontWeight: 400 }}>
                  Shown to recipient on PayPal/Venmo
                </span>
              </label>
              <input className="form-input" value={note} onChange={e => setNote(e.target.value)}
                placeholder="March Madness 2026" />
            </div>
          </div>
        </div>

        {/* Right: payment methods */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Amount summary */}
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>
              Total to Pay
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.5rem', color: 'var(--blue)', lineHeight: 1 }}>
              ${amountNum}
            </div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', marginTop: 6 }}>
              {selected.size} bracket{selected.size !== 1 ? 's' : ''} selected &times; ${fee}
            </div>
          </div>

          {/* PayPal */}
          {hasPayPal && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: '1.8rem' }}>&#x1F4B3;</div>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--cream)', letterSpacing: '1px' }}>PAYPAL</div>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)' }}>Opens PayPal with amount pre-filled</div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={handlePayPal}
                disabled={amountNum <= 0}
                style={{ width: '100%', justifyContent: 'center', background: '#0070ba', borderColor: '#0070ba' }}>
                Pay ${amountNum} via PayPal
              </button>
            </div>
          )}

          {/* Venmo */}
          {hasVenmo && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: '1.8rem' }}>&#x1F4F1;</div>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--cream)', letterSpacing: '1px' }}>VENMO</div>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)' }}>Opens Venmo with amount and note pre-filled</div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleVenmo}
                disabled={amountNum <= 0}
                style={{ width: '100%', justifyContent: 'center', background: '#3d95ce', borderColor: '#3d95ce' }}>
                Pay ${amountNum} via Venmo
              </button>
            </div>
          )}

          {/* Physical / other */}
          {hasContact && (
            <div className="card" style={{ background: 'rgba(75,139,190,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ fontSize: '1.8rem' }}>&#x2709;&#xFE0F;</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--cream)', letterSpacing: '1px' }}>OTHER / PHYSICAL</div>
              </div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)', lineHeight: 1.5 }}>
                To pay by check or other method, please contact the pool administrator at{' '}
                <a href={`mailto:${config.contactEmail}`} style={{ color: 'var(--blue)' }}>
                  {config.contactEmail}
                </a>
              </div>
            </div>
          )}

          {/* Note about confirmation */}
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gray)',
            padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.07)', lineHeight: 1.5 }}>
            After completing payment, the pool admin will verify and mark your bracket(s) as paid. You will see a
            <strong style={{ color: 'var(--green)' }}> Paid</strong> badge on your Dashboard once confirmed.
          </div>

        </div>
      </div>
    </div>
  );
}
